import React, { useState, useEffect } from 'react';
import {
  Pane,
  Card,
  Badge,
  Text,
  Heading,
  Spinner,
  toaster,
  Table,
  Strong,
  Icon,
  CircleIcon,
  GlobeIcon,
  DesktopIcon,
  SearchIcon,
  FilterListIcon,
  SelectMenu,
  TextInput,
  CrossIcon,
  Button,
  Tooltip,
  Alert,
  IconButton,
  ChevronRightIcon
} from 'evergreen-ui';
import api from '../services/api';
import { useProject } from '../context/ProjectContext';

const Dashboard = () => {
  const { selectProject, currentProject } = useProject();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toaster.danger(
        error.response?.data?.detail || 
        'Failed to fetch dashboard data'
      );
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'red',
      'high': 'orange',
      'medium': 'yellow',
      'low': 'green',
      'info': 'blue'
    };
    return colors[severity] || 'neutral';
  };

  const getSeverityBackgroundColor = (severity) => {
    const colors = {
      'critical': '#FFE8E6',
      'high': '#FFF4E6',
      'medium': '#FFFBF0',
      'low': '#E6F4EA',
      'info': '#E6F3FF'
    };
    return colors[severity] || '#F7F8FA';
  };

  const getTotalFindings = (findingsBySeverity) => {
    return Object.values(findingsBySeverity).reduce((sum, count) => sum + count, 0);
  };

  const getHighestSeverity = (findingsBySeverity) => {
    const severities = ['critical', 'high', 'medium', 'low', 'info'];
    for (const severity of severities) {
      if (findingsBySeverity[severity] > 0) {
        return severity;
      }
    }
    return 'none';
  };

  const filteredProjects = dashboardData?.projects?.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = selectedSeverity === 'all' || 
      (selectedSeverity === 'none' && getTotalFindings(project.findings_by_severity) === 0) ||
      project.findings_by_severity[selectedSeverity] > 0;
    
    return matchesSearch && matchesSeverity;
  }) || [];

  const handleProjectSelect = (project) => {
    selectProject(project);
    toaster.success(`Selected project: ${project.name}`);
  };

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh">
        <Spinner size={48} />
      </Pane>
    );
  }

  if (!dashboardData) {
    return (
      <Pane padding={16}>
        <Alert
          intent="danger"
          title="Failed to load dashboard"
          marginBottom={16}
        >
          Unable to load dashboard data. Please try refreshing the page.
        </Alert>
      </Pane>
    );
  }

  return (
    <Pane padding={16}>
      {/* Header */}
      <Pane 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        marginBottom={24}
      >
        <Heading size={800}>Dashboard</Heading>
        <Button
          appearance="primary"
          iconBefore={CircleIcon}
          onClick={fetchDashboard}
        >
          Refresh
        </Button>
      </Pane>

      {/* Overall Stats Cards */}
      <Pane display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={16} marginBottom={32}>
        <Card
          padding={16}
          background="tint1"
          border="default"
          elevation={1}
        >
          <Pane display="flex" alignItems="center" justifyContent="space-between">
            <Pane>
              <Text size={300} color="muted">Total Projects</Text>
              <Heading size={600}>{dashboardData.total_projects}</Heading>
            </Pane>
            <Icon icon={CircleIcon} size={24} color="default" />
          </Pane>
        </Card>

        <Card
          padding={16}
          background="tint1"
          border="default"
          elevation={1}
        >
          <Pane display="flex" alignItems="center" justifyContent="space-between">
            <Pane>
              <Text size={300} color="muted">Total Findings</Text>
              <Heading size={600}>{dashboardData.total_findings}</Heading>
            </Pane>
            <Icon icon={SearchIcon} size={24} color="red" />
          </Pane>
        </Card>

        <Card
          padding={16}
          background="tint1"
          border="default"
          elevation={1}
        >
          <Pane display="flex" alignItems="center" justifyContent="space-between">
            <Pane>
              <Text size={300} color="muted">Total Hosts</Text>
              <Heading size={600}>{dashboardData.total_hosts}</Heading>
              {dashboardData.total_checked_hosts !== undefined && (
                <Text size={300} color="muted" marginTop={4}>
                  Checked: {dashboardData.total_checked_hosts} / {dashboardData.total_hosts}
                </Text>
              )}
            </Pane>
            <Icon icon={DesktopIcon} size={24} color="blue" />
          </Pane>
        </Card>

        <Card
          padding={16}
          background="tint1"
          border="default"
          elevation={1}
        >
          <Pane display="flex" alignItems="center" justifyContent="space-between">
            <Pane>
              <Text size={300} color="muted">Total Domains</Text>
              <Heading size={600}>{dashboardData.total_domains}</Heading>
            </Pane>
            <Icon icon={GlobeIcon} size={24} color="green" />
          </Pane>
        </Card>
      </Pane>

      {/* Statistics Row */}
      <Pane display="grid" gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={16} marginBottom={24}>
        {/* Overall Findings by Severity */}
        <Card padding={16} background="tint1" border="default" elevation={1}>
          <Heading size={500} marginBottom={16}>Overall Findings by Severity</Heading>
          <Pane display="flex" gap={8} flexWrap="wrap">
            {Object.entries(dashboardData.overall_findings_by_severity).map(([severity, count]) => (
              <Badge
                key={severity}
                color={getSeverityColor(severity)}
                isSolid
                fontSize={12}
              >
                {severity.toUpperCase()}: {count}
              </Badge>
            ))}
          </Pane>
        </Card>

        {/* Countries with Host Distribution */}
        <Card padding={16} background="tint1" border="default" elevation={1}>
          <Heading size={500} marginBottom={16}>Host Distribution by Country</Heading>
          {dashboardData.countries && dashboardData.countries.length > 0 ? (
            <Pane display="flex" flexDirection="column" gap={8}>
              {dashboardData.countries.slice(0, 5).map((country, index) => (
                <Pane key={country.country} display="flex" alignItems="center" justifyContent="space-between">
                  <Pane display="flex" alignItems="center" gap={8}>
                    <img 
                      src="/country.png" 
                      alt="Country" 
                      style={{ width: 16, height: 16 }}
                    />
                    <Text fontSize={12} fontWeight={500}>
                      {country.country}
                    </Text>
                  </Pane>
                  <Pane display="flex" alignItems="center" gap={8}>
                    <Text fontSize={12} color="muted">
                      {country.host_count} hosts ({country.percentage}%)
                    </Text>
                    <Badge
                      color={index === 0 ? 'blue' : index === 1 ? 'green' : 'neutral'}
                      fontSize={10}
                    >
                      {country.percentage}%
                    </Badge>
                  </Pane>
                </Pane>
              ))}
              {dashboardData.countries.length > 5 && (
                <Text fontSize={12} color="muted" textAlign="center">
                  +{dashboardData.countries.length - 5} more countries
                </Text>
              )}
            </Pane>
          ) : (
            <Text fontSize={12} color="muted">No country data available</Text>
          )}
        </Card>
      </Pane>

      {/* Search and Filter */}
      <Pane 
        display="flex" 
        alignItems="center" 
        gap={16} 
        marginBottom={24}
        flexWrap="wrap"
      >
        <Pane display="flex" alignItems="center" gap={8}>
          <TextInput
            placeholder="Search projects..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            width={250}
          />
          {searchTerm && (
            <IconButton
              icon={CrossIcon}
              size="small"
              onClick={() => setSearchTerm('')}
            />
          )}
        </Pane>

        <SelectMenu
          title="Filter by severity"
          options={[
            { label: 'All Severities', value: 'all' },
            { label: 'Critical', value: 'critical' },
            { label: 'High', value: 'high' },
            { label: 'Medium', value: 'medium' },
            { label: 'Low', value: 'low' },
            { label: 'Info', value: 'info' },
            { label: 'No Findings', value: 'none' }
          ]}
          selected={selectedSeverity}
          onSelect={item => setSelectedSeverity(item.value)}
        >
          <Button iconAfter={FilterListIcon}>
            Severity: {selectedSeverity === 'all' ? 'All' : selectedSeverity.charAt(0).toUpperCase() + selectedSeverity.slice(1)}
          </Button>
        </SelectMenu>
      </Pane>

      {/* Projects Table */}
      {filteredProjects.length === 0 ? (
        <Card
          padding={32}
          background="tint1"
          border="default"
          elevation={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text size={500} color="muted">
            {dashboardData.projects.length === 0 
              ? 'No projects found. Create your first project to get started.'
              : 'No projects match your search criteria.'
            }
          </Text>
        </Card>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Project</Table.TextHeaderCell>
            <Table.TextHeaderCell>Findings</Table.TextHeaderCell>
            <Table.TextHeaderCell>Hosts</Table.TextHeaderCell>
            <Table.TextHeaderCell>Domains</Table.TextHeaderCell>
            <Table.TextHeaderCell>URLs</Table.TextHeaderCell>
            <Table.TextHeaderCell>Ports</Table.TextHeaderCell>
            <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {filteredProjects.map((project) => {
              const totalFindings = getTotalFindings(project.findings_by_severity);
              const highestSeverity = getHighestSeverity(project.findings_by_severity);
              
              return (
                <Table.Row 
                  key={project.id}
                  isSelectable
                  isSelected={currentProject?.id === project.id}
                  onSelect={() => handleProjectSelect(project)}
                >
                  <Table.Cell>
                    <Pane>
                      <Strong>{project.name}</Strong>
                      {currentProject?.id === project.id && (
                        <Badge color="blue" marginLeft={8}>Current</Badge>
                      )}
                    </Pane>
                  </Table.Cell>
                  
                  <Table.Cell>
                    <Pane display="flex" alignItems="center" gap={8}>
                      {totalFindings > 0 ? (
                        <Badge
                          color={getSeverityColor(highestSeverity)}
                          isSolid
                        >
                          {totalFindings}
                        </Badge>
                      ) : (
                        <Text color="muted">0</Text>
                      )}
                      {totalFindings > 0 && (
                        <Pane display="flex" gap={4}>
                          {Object.entries(project.findings_by_severity).map(([severity, count]) => 
                            count > 0 ? (
                              <Tooltip key={severity} content={`${severity}: ${count}`}>
                                <Badge
                                  color={getSeverityColor(severity)}
                                  fontSize={10}
                                >
                                  {count}
                                </Badge>
                              </Tooltip>
                            ) : null
                          )}
                        </Pane>
                      )}
                    </Pane>
                  </Table.Cell>
                  
                  <Table.TextCell>
                    {project.unique_hosts}
                    {project.checked_hosts !== undefined && project.checked_hosts > 0 && (
                      <Text size={300} color="muted" marginTop={2}>
                        ({project.checked_hosts} checked)
                      </Text>
                    )}
                  </Table.TextCell>
                  <Table.TextCell>{project.unique_domains}</Table.TextCell>
                  <Table.TextCell>{project.unique_urls}</Table.TextCell>
                  <Table.TextCell>{project.total_ports}</Table.TextCell>
                  
                  <Table.Cell>
                    <IconButton
                      icon={ChevronRightIcon}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectSelect(project);
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Pane>
  );
};

export default Dashboard; 